import { Component, HostBinding, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock, StudyFacts, paletteColors } from '../report-aggregation';
import type { BlockPatch } from '../report-design-edit';
import { SummaryBox, textOn } from './summary-boxes.component';

/** The four figures, in the order they are drawn. Each is the override key on the block. */
type MethodologyField = 'sampleSize' | 'medianLoi' | 'incidenceRate' | 'market';

/** A stretch of a line, bold or not. */
export interface TextRun {
  text: string;
  bold: boolean;
}

/** Either a run of bullets or a paragraph -- never both, so the template can branch on one field. */
interface NotesBlock {
  bullets: TextRun[][];
  text: TextRun[];
}

// Hyphen, en dash, asterisk or an actual bullet, followed by a space or nothing at all -- the
// latter being what a bullet looks like mid-typing. Typing "- " is the documented form; the others
// are what people paste in from elsewhere.
const BULLET = /^\s*[-–—*•](\s+|$)/;

// Text wrapped in two asterisks or one, the way it is typed in a chat or a doc. The wrapped text
// must start and end on a character -- "5 * 3 * 2" is arithmetic, not emphasis -- and holds no
// asterisk of its own, so "**" can never be read as one empty pair of singles.
const BOLD = /\*\*([^\s*](?:[^*]*[^\s*])?)\*\*|\*([^\s*](?:[^*]*[^\s*])?)\*/;

/**
 * A line split into its bold and plain stretches. An asterisk that pairs with nothing stays as
 * typed, so a footnote mark or a lone "460*" is left alone.
 *
 * Split rather than turned into markup: the notes are typed into the deck and shown to a client, and
 * handing typed text to the browser as HTML is the one thing never to do with it.
 */
export function boldRuns(line: string): TextRun[] {
  const runs: TextRun[] = [];
  // Made global here rather than on the constant: a global pattern carries its lastIndex from one
  // call into the next.
  const pattern = new RegExp(BOLD.source, 'g');
  let at = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(line))) {
    const start = match.index;
    if (start > at) runs.push({ text: line.slice(at, start), bold: false });
    runs.push({ text: match[1] ?? match[2], bold: true });
    at = start + match[0].length;
  }
  if (at < line.length || !runs.length) runs.push({ text: line.slice(at), bold: false });
  return runs;
}

/**
 * Plain text to renderable blocks. Consecutive bullet lines become one list rather than a stack of
 * one-item lists; anything else is a paragraph. Blank lines only separate.
 */
function parseNotes(notes: string): NotesBlock[] {
  const blocks: NotesBlock[] = [];

  for (const raw of notes.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;

    if (BULLET.test(line)) {
      const item = line.replace(BULLET, '');
      if (!item) continue;
      const last = blocks[blocks.length - 1];
      if (last?.bullets.length) last.bullets.push(boldRuns(item));
      else blocks.push({ bullets: [boldRuns(item)], text: [] });
      continue;
    }

    blocks.push({ bullets: [], text: boldRuns(line) });
  }

  return blocks;
}

@Component({
  selector: 'app-methodology-block',
  standalone: false,
  template: `
    <app-summary-boxes
      [boxes]="boxes"
      [editing]="editing"
      (valueChange)="set($event.field, $event.value)">
    </app-summary-boxes>

    @if (editing || notesHeading || notesBlocks.length) {
      <div class="notes mt-3">
        @if (editing) {
          <input type="text"
                 class="notes-edit nav-font16 font-weight-bold w-100 mb-2"
                 maxlength="120"
                 placeholder="Heading (optional)"
                 aria-label="Notes heading"
                 [value]="notesHeading"
                 (input)="set('notesHeading', $any($event.target).value)">
          <textarea class="notes-edit nav-font14 w-100"
                    rows="5"
                    placeholder="Notes. Start a line with &quot;- &quot; to make it a bullet, and wrap words in *asterisks* for bold."
                    aria-label="Notes"
                    [value]="notes"
                    (input)="set('notes', $any($event.target).value)"></textarea>
        } @else {
          @if (notesHeading) {
            <p class="nav-secondary nav-font16 font-weight-bold mb-2">{{ notesHeading }}</p>
          }
          @for (part of notesBlocks; track $index) {
            @if (part.bullets.length) {
              <ul class="nav-font14 mb-2">
                @for (bullet of part.bullets; track $index) {
                  <li>@for (run of bullet; track $index) {@if (run.bold) {<strong>{{ run.text }}</strong>} @else {<span>{{ run.text }}</span>}}</li>
                }
              </ul>
            } @else {
              <p class="nav-font14 mb-2">@for (run of part.text; track $index) {@if (run.bold) {<strong>{{ run.text }}</strong>} @else {<span>{{ run.text }}</span>}}</p>
            }
          }
        }
      </div>
    }
  `,
  styles: [`
    :host { display: block; }
    /* On the card's white background rather than a palette box, so the ordinary grey treatment
       applies here instead of the boxes' currentColor one. */
    .notes-edit {
      background: transparent;
      border: 1px solid var(--nav-grey1);
      border-radius: 3px;
      color: inherit;
      padding: 2px 4px;
    }
    .notes-edit:hover {
      border-color: var(--nav-grey2);
    }
    .notes-edit:focus {
      background: var(--nav-true-white);
      border-color: var(--nav-grey3);
      outline: none;
    }
    .notes ul {
      padding-left: 1.25rem;
    }
  `]
})
export class MethodologyBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;
  @Input() studyFacts?: StudyFacts;
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  /**
   * Only across a whole row. At half width the boxes are already narrow, and taking 3rem off each
   * side would squeeze them further. Optional chaining because a host binding is evaluated before
   * the first ngOnChanges on the very first pass.
   */
  @HostBinding('class.pl-5')
  @HostBinding('class.pr-5')
  get fullWidth(): boolean {
    return this.block?.segmentLocation === 'full';
  }

  boxes: SummaryBox[] = [];
  notesHeading = '';
  notes = '';
  /** Runs of consecutive bullet lines, and the plain paragraphs between them, in order. */
  notesBlocks: NotesBlock[] = [];

  ngOnChanges() {
    if (this.block.reportType !== 'study-overview') return;
    const { sampleSize, medianLoi, incidenceRate, market } = this.block.reportExtraData;
    const facts = this.studyFacts;

    this.notesHeading = this.block.reportExtraData.notesHeading ?? '';
    this.notes = this.block.reportExtraData.notes ?? '';
    this.notesBlocks = parseNotes(this.notes);

    // Computed unless overridden -- except Market, which no export carries, so it only ever shows
    // what was typed. Incidence needs the screen-out counts recorded at ingestion, since data.json
    // holds qualified respondents only. Labels are fixed, unlike an executive summary's: these name
    // figures with a standard meaning.
    const [primary, secondary, accent, positive] = paletteColors(this.palette);
    this.boxes = [
      this.box('sampleSize', 'Sample size', sampleSize, facts ? String(facts.sampleSize) : '', primary),
      this.box('medianLoi', 'Median LOI', medianLoi, this.loi(facts), secondary),
      this.box('incidenceRate', 'Incidence', incidenceRate, this.incidence(facts), accent),
      this.box('market', 'Market', market, '', positive),
    ];
  }

  set(field: string, value: string) {
    this.onPatch?.({ [field]: value });
  }

  private incidence(facts?: StudyFacts): string {
    return facts?.incidenceRatePercent == null ? '' : `${Math.round(facts.incidenceRatePercent)}%`;
  }

  /** Carries its own unit, since the label no longer does. A typed override shows exactly as typed. */
  private loi(facts?: StudyFacts): string {
    return facts?.medianLoiMinutes == null ? '' : `${Math.round(facts.medianLoiMinutes)} min`;
  }

  private box(
    field: MethodologyField,
    label: string,
    override: string | undefined,
    computed: string,
    background: string,
  ): SummaryBox {
    return { field, label, override: override ?? '', computed, background, color: textOn(background) };
  }
}
